import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { getFingerprint } from '../utils/fingerprint'
import { onboardingApi, ApiError } from '../services/api'
import { useLang } from '../../i18n/LanguageContext'
import StepIndicator from '../components/StepIndicator'
import Step1Welcome from '../steps/Step1Welcome'
import Step2Organization from '../steps/Step2Organization'
import Step3Contact from '../steps/Step3Contact'
import Step4Account from '../steps/Step4Account'
import Step5Review from '../steps/Step5Review'
import StepSuccess from '../steps/StepSuccess'

/**
 * Reads a File as base64 (without the data:...;base64, prefix) so it can go in
 * the JSON onboarding payload. The logo travels as base64 because at this
 * point no tenant or admin session exists to use the normal media API with —
 * Identity forwards it to the media library after creating the tenant.
 */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result || ''
      const commaIdx = result.indexOf(',')
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result)
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

export default function OnboardingWizard() {
  const { token } = useParams()
  const { t, locale } = useLang()

  // ── State ──
  const [status, setStatus] = useState('loading') // loading | ready | error | success
  const [errorMessage, setErrorMessage] = useState('')
  const [tokenData, setTokenData] = useState(null)
  const [step, setStep] = useState(1)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [result, setResult] = useState(null)

  const [formData, setFormData] = useState({
    // Step 2: Organization
    tenantName: '',
    slug: '',
    slugManual: false,
    shortName: '',
    customDomain: '',
    orgNumber: '',
    countryCode: 'NO',
    subscriptionPlan: 'free',
    template: 'classic',
    tagline: '',
    logoUrl: '',
    logoFile: null,     // File chosen in the upload control
    logoPreview: '',    // data URL for the thumbnail
    // Step 3: Contact
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    // Postal address
    postalAddress: '',
    postalCity: '',
    postalCode: '',
    // Visiting address (may differ from postal)
    visitingAddress: '',
    visitingCity: '',
    visitingPostalCode: '',
    sameAsPostalAddress: true,
    // Step 4: Account
    ownerUsername: '',
    ownerPassword: '',
    ownerPasswordConfirm: '',
  })

  // ── Claim token on mount ──
  useEffect(() => {
    let cancelled = false

    async function claimToken() {
      try {
        const fingerprint = await getFingerprint()
        const data = await onboardingApi.claimToken(token, fingerprint)

        if (cancelled) return

        setTokenData(data)

        // Pre-fill from token data
        setFormData(prev => ({
          ...prev,
          tenantName: data.tenantName || prev.tenantName,
          contactEmail: data.email || prev.contactEmail,
          slug: data.tenantName ? onboardingApi.slugify(data.tenantName) : prev.slug,
        }))

        setStatus('ready')
      } catch (err) {
        if (cancelled) return

        if (err instanceof ApiError && err.status === 403) {
          // The link is tied to the first device that opened it (the server says so in Norwegian only).
          setErrorMessage(t('onboarding.deviceBound'))
        } else if (err instanceof ApiError) {
          setErrorMessage(err.message)
        } else {
          setErrorMessage(t('onboarding.connectError'))
        }
        setStatus('error')
      }
    }

    claimToken()
    return () => { cancelled = true }
  }, [token])

  // ── Handlers ──
  const updateForm = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }))
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    setSubmitError('')

    try {
      const fingerprint = await getFingerprint()

      // An uploaded file wins over a pasted URL — the file is what the user
      // saw in the preview.
      let logoBase64 = null
      let logoContentType = null
      let logoFileName = null
      if (formData.logoFile) {
        logoBase64 = await fileToBase64(formData.logoFile)
        logoContentType = formData.logoFile.type || 'image/png'
        logoFileName = formData.logoFile.name || 'logo.png'
      }

      const payload = {
        fingerprint,
        tenantName: formData.tenantName.trim(),
        slug: formData.slug.trim(),
        shortName: formData.shortName.trim(),
        customDomain: formData.customDomain?.trim() || null,
        orgNumber: formData.orgNumber?.trim() || null,
        countryCode: formData.countryCode || 'NO',
        contactName: formData.contactName.trim(),
        contactEmail: formData.contactEmail.trim(),
        contactPhone: formData.contactPhone?.trim() || null,
        postalAddress: formData.postalAddress?.trim() || null,
        postalCode: formData.postalCode?.trim() || null,
        postalCity: formData.postalCity?.trim() || null,
        visitingAddress: formData.sameAsPostalAddress
          ? formData.postalAddress?.trim() || null
          : formData.visitingAddress?.trim() || null,
        visitingPostalCode: formData.sameAsPostalAddress
          ? formData.postalCode?.trim() || null
          : formData.visitingPostalCode?.trim() || null,
        visitingCity: formData.sameAsPostalAddress
          ? formData.postalCity?.trim() || null
          : formData.visitingCity?.trim() || null,
        subscriptionPlan: formData.subscriptionPlan || 'free',
        template: formData.template || 'classic',
        tagline: formData.tagline?.trim() || null,
        // The wizard language becomes the tenant's default email/SMS culture.
        culture: locale,
        logoUrl: logoBase64 ? null : (formData.logoUrl?.trim() || null),
        logoBase64,
        logoContentType,
        logoFileName,
        ownerUsername: formData.ownerUsername.trim(),
        ownerPassword: formData.ownerPassword,
      }

      const res = await onboardingApi.completeOnboarding(token, payload)
      setResult(res)
      setStatus('success')
    } catch (err) {
      if (err instanceof ApiError) {
        setSubmitError(err.message)
      } else {
        setSubmitError(t('onboarding.genericError'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  // ── Render ──

  // Loading state
  if (status === 'loading') {
    return (
      <div className="wizard">
        <div className="wizard__card">
          <div className="loading-page">
            <div className="spinner" />
            <div className="loading-page__text">{t('onboarding.validating')}</div>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="wizard">
        <div className="wizard__card">
          <div className="wizard__body">
            <div className="error-page">
              <div className="error-page__icon">✕</div>
              <h1 className="wizard__title" style={{ textAlign: 'center' }}>
                {t('onboarding.invalidTitle')}
              </h1>
              <p className="wizard__subtitle" style={{ textAlign: 'center' }}>
                {errorMessage}
              </p>
              <p style={{ textAlign: 'center', color: 'var(--color-gray-400)', fontSize: 'var(--font-size-sm)' }}>
                {t('onboarding.invalidContact')}
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Success state
  if (status === 'success') {
    return (
      <div className="wizard">
        <div className="wizard__card">
          <StepSuccess result={result} data={formData} />
        </div>
      </div>
    )
  }

  // Wizard steps
  return (
    <div className="wizard">
      <div className="wizard__card">
        <StepIndicator current={step} />

        {step === 1 && (
          <Step1Welcome
            tokenData={tokenData}
            onNext={() => setStep(2)}
          />
        )}

        {step === 2 && (
          <Step2Organization
            data={formData}
            onChange={updateForm}
            onNext={() => setStep(3)}
            onBack={() => setStep(1)}
          />
        )}

        {step === 3 && (
          <Step3Contact
            data={formData}
            onChange={updateForm}
            onNext={() => setStep(4)}
            onBack={() => setStep(2)}
          />
        )}

        {step === 4 && (
          <Step4Account
            data={formData}
            onChange={updateForm}
            onNext={() => setStep(5)}
            onBack={() => setStep(3)}
          />
        )}

        {step === 5 && (
          <Step5Review
            data={formData}
            onBack={() => setStep(4)}
            onSubmit={handleSubmit}
            submitting={submitting}
            error={submitError}
          />
        )}
      </div>
    </div>
  )
}
